import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Logo from './imagens/logo_original.png';
import HeroImage from './imagens/hero-mulher.png';
import Card1 from './imagens/card1.jpg';
import Card2 from './imagens/card2.jpg';
import Card3 from './imagens/card3.jpg';
import Card4 from './imagens/card4.jpg';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function FeaturePanel() {
  const cards = [
    { src: Card1, rotate: -12, top: '5%',   left: '-12%',  delay: 0.3 },
    { src: Card2, rotate: 10,  top: '2%',   right: '-30%', delay: 0.45 },
    { src: Card3, rotate: -6,  top: '45%',  left: '-13%',  delay: 0.6 },
    { src: Card4, rotate: 8,   top: '42%',  right: '-30%', delay: 0.75 },
  ];

  return (
    <section className="relative flex h-full flex-col items-center justify-center overflow-inherit py-8 lg:py-0">
      {/* Hero image container with floating badges */}
      <div className="relative mx-auto w-full max-w-sm">
        {/* Scattered cards — behind the woman (z-0) */}
        {cards.map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.7, rotate: card.rotate * 0.5 }}
            animate={{ opacity: 1, scale: 1, rotate: card.rotate }}
            transition={{ duration: 0.6, delay: card.delay, type: 'spring', stiffness: 80 }}
            className="absolute z-0"
            style={{
              top: card.top,
              ...(card.left ? { left: card.left } : {}),
              ...(card.right ? { right: card.right } : {}),
              width: '42%',
            }}
          >
            <img
              src={card.src}
              alt=""
              className="w-full rounded-xl shadow-lg"
              style={{
                border: '3px solid rgba(255,255,255,0.6)',
                boxShadow: '0 8px 30px rgba(63, 4, 92, 0.2)',
              }}
            />
          </motion.div>
        ))}

        {/* Hero image — in front of cards (z-10) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative z-10"
        >
          <img
            src={HeroImage}
            alt="Mulher acessando a plataforma V\u00eddeoOferta no celular"
            className="mx-auto w-full max-w-xs drop-shadow-2xl sm:max-w-sm"
            style={{
              filter: 'drop-shadow(0 20px 40px rgba(63, 4, 92, 0.18))',
              maskImage: 'linear-gradient(to bottom, black 65%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 81%, transparent 100%)'
            }}
          />
        </motion.div>

        {/* Headline — overlapping the faded bottom of the image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="absolute z-20"
            style={{ top: '61%', left: '20%' }}
          >
            <h1 className="hero-title">Crie ofertas em vídeo</h1>
          </motion.div>

        {/* Badge: Templates prontos */}
        <motion.div
          initial={{ opacity: 0, x: -20, y: -10 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5, type: 'spring', stiffness: 120 }}
          className="absolute left-0 top-6 z-30 sm:-left-4 lg:-left-6"
        >
          <div className="flex items-center gap-2.5 rounded-2xl border border-flow-border bg-white px-4 pt-[2px] pb-[2px] shadow-lg">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-flow-brand-soft text-flow-brand">
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path d="M4 5h16v14H4z" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M7 9h10M7 13h6" stroke="currentColor" strokeWidth="2" />
              </svg>
            </span>
            <span className="text-sm font-semibold text-flow-ink">Templates prontos</span>
          </div>
        </motion.div>

        {/* Badge: Baixe e publique */}
        <motion.div
          initial={{ opacity: 0, x: 20, y: 10 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, type: 'spring', stiffness: 120 }}
          className="absolute right-0 top-6 md:top-auto md:bottom-16 z-30 lg:-right-6"
        >
          <div className="flex items-center gap-2.5 rounded-2xl border border-flow-border bg-white px-4 pt-[2px] pb-[2px] shadow-lg">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-flow-brand-soft text-flow-brand">
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="text-sm font-semibold text-flow-ink">Baixe e publique</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function LoginForm({ loading, onSubmit, message, setMessage }) {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [touched, setTouched] = useState({ email: false, senha: false });
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(() => {
    const next = {};
    if (!email) {
      next.email = 'Informe seu e-mail.';
    } else if (!emailRegex.test(email)) {
      next.email = 'Digite um e-mail válido.';
    }
    if (!senha) {
      next.senha = 'Informe sua senha.';
    } else if (senha.length < 6) {
      next.senha = 'Use ao menos 6 caracteres.';
    }
    return next;
  }, [email, senha]);

  const isFormValid = Object.keys(errors).length === 0;
  const showEmailError = (touched.email || submitted) && errors.email;
  const showSenhaError = (touched.senha || submitted) && errors.senha;

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (!isFormValid) {
      setMessage({ type: 'error', text: 'Revise os campos para continuar.' });
      return;
    }
    setMessage(null);
    onSubmit({ email, senha, rememberMe });
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit} noValidate>
      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium text-flow-ink">
          E-mail
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-flow-ink placeholder-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-flow-brand ${
            showEmailError ? 'border-red-400/70 focus-visible:ring-red-400' : 'border-flow-border focus-visible:border-flow-brand'
          }`}
          placeholder="voce@exemplo.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
          aria-invalid={Boolean(showEmailError)}
          aria-describedby={showEmailError ? 'email-error' : undefined}
          required
        />
        {showEmailError && (
          <p id="email-error" className="text-xs text-red-300">
            {errors.email}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="senha" className="text-sm font-medium text-flow-ink">
          Senha
        </label>
        <div className="relative">
          <input
            id="senha"
            name="senha"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            className={`w-full rounded-xl border bg-white px-4 py-3 pr-12 text-sm text-flow-ink placeholder-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-flow-brand ${
              showSenhaError ? 'border-red-400/70 focus-visible:ring-red-400' : 'border-flow-border focus-visible:border-flow-brand'
            }`}
            placeholder="••••••••"
            value={senha}
            onChange={(event) => setSenha(event.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, senha: true }))}
            aria-invalid={Boolean(showSenhaError)}
            aria-describedby={showSenhaError ? 'senha-error' : undefined}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute inset-y-0 right-3 flex items-center text-xs font-medium text-flow-muted transition hover:text-flow-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
            aria-pressed={showPassword}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {showPassword ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>
        {showSenhaError && (
          <p id="senha-error" className="text-xs text-red-300">
            {errors.senha}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-flow-muted">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-flow-border bg-white text-flow-brand focus-visible:ring-2 focus-visible:ring-flow-brand"
            checked={rememberMe}
            onChange={(event) => setRememberMe(event.target.checked)}
          />
          Lembrar-me
        </label>
        <Link
          to="/forgot-password"
          className="font-medium text-flow-brand transition hover:text-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
        >
          Esqueci minha senha
        </Link>
      </div>

      <button
        type="submit"
        disabled={!isFormValid || loading}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-flow-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand disabled:cursor-not-allowed disabled:opacity-60"
        aria-disabled={!isFormValid || loading}
      >
        {loading ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
            Entrando...
          </>
        ) : (
          'Entrar'
        )}
      </button>

      {message && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            message.type === 'error'
              ? 'border-red-400/40 bg-red-500/10 text-red-600'
              : 'border-flow-brand/30 bg-flow-brand-soft text-flow-brand'
          }`}
          role={message.type === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          {message.text}
        </div>
      )}

      <p className="text-center text-sm text-flow-muted">
        Não possui uma conta?{' '}
        <Link
          to="/cadastrar"
          className="font-semibold text-flow-brand transition hover:text-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
        >
          Cadastrar
        </Link>
      </p>

      <p className="mt-5 text-center">
        <Link
          to="/solicitar-exclusao"
          className="text-xs text-flow-muted/30 transition hover:text-flow-muted/60 focus-visible:outline-none"
          tabIndex={-1}
        >
          Exclusão de dados · LGPD
        </Link>
      </p>
    </form>
  );
}

function LoginPage() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const abortRef = useRef(null);

  const abortInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const processLogin = (data) => {
    setLoading(false);
    if (data.success) {
      sessionStorage.setItem('user', JSON.stringify(data.user));
      window.location.href = data.ultimo_video ? `/?id=${data.ultimo_video}` : '/';
    } else {
      setMessage({ type: 'error', text: data.error || 'Não foi possível autenticar.' });
    }
  };

  const handleSubmit = async ({ email, senha, rememberMe }) => {
    abortInFlight();
    setMessage(null);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch('https://videooferta.com/api/entrar.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, senha }),
        signal: controller.signal
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      processLogin(data);
    } catch (err) {
      if (err?.name === 'AbortError') {
        return;
      }
      setLoading(false);
      setMessage({ type: 'error', text: 'Erro ao conectar com o servidor.' });
    } finally {
      abortRef.current = null;
      void rememberMe;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      abortInFlight();
      setMessage(null);
      setLoading(true);

      const controller = new AbortController();
      abortRef.current = controller;

      fetch('https://videooferta.com/api/login-token.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
        signal: controller.signal
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
          return res.json();
        })
        .then(processLogin)
        .catch((err) => {
          if (err?.name === 'AbortError') {
            return;
          }
          setMessage({ type: 'error', text: 'Erro ao autenticar com token.' });
          setLoading(false);
        })
        .finally(() => {
          abortRef.current = null;
        });
    }
    return () => abortInFlight();
  }, [abortInFlight, location.search]);

  return (
    <div className="relative min-h-screen bg-dot-grid overflow-hidden">
      <main className="relative mx-auto flex min-h-screen max-w-6xl flex-col gap-10 px-6 py-12 lg:flex-row lg:items-center">
        <div className="order-2 w-full lg:order-1 lg:w-1/2">
          <FeaturePanel />
        </div>

        <div className="order-1 w-full lg:order-2 lg:w-1/2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="mx-auto w-full max-w-md rounded-2xl border border-flow-border bg-white p-6 sm:p-8"
          >
            <div className="mb-6 flex items-center justify-center">
              <img src={Logo} alt="VídeoOferta" className="h-12 w-auto" />
            </div>

            <div className="mb-6 space-y-2">
              <h2 className="text-2xl font-semibold text-flow-ink">Entrar</h2>
              <p className="text-sm text-flow-muted">
                Acesse sua conta para gerenciar seus vídeos.
              </p>
            </div>

            <LoginForm
              loading={loading}
              onSubmit={handleSubmit}
              message={message}
              setMessage={setMessage}
            />
          </motion.div>
        </div>
      </main>
    </div>
  );
}

export default LoginPage;

// Tailwind config (resumo): inclui cores flow (green/green-dark/black/white/aqua-900/blue-500/blue-100) e radius 16/20px.
